import { realizePath } from './surveyBranching.js';
import type { SurveyResultsDto, SurveyResultRecipient } from '../types/surveyResults.js';
import type { SurveyStatus, SurveyStep, QuestionStep } from '../types/survey.js';
import type { SurveyResponse } from '../types/surveyResponse.js';
import type { AccountType } from '../types/account.js';

// Phase 6B Phase E — pure results aggregation, run client-side (mirrors the
// broadcast adapter). The Survey rollup counters are a cache; the TRUTH is the
// recipient/response rows, so the metrics here are derived from those and the
// rollups are used only where a row-level value isn't shipped. Per-channel +
// per-question aggregation land in later E commits on top of this module.

export type SurveyMetrics = {
  sent: number; // total invites dispatched across channels
  opened: number; // unique recipients who opened the invite email
  started: number; // recipients who viewed the survey page (≥1 page view)
  submitted: number; // completed responses
  pending: number; // recipients not yet submitted
  pageViews: number; // total page views across recipients
  // Rates (0–100, rounded) for the secondary metric on cards; null when no base.
  openRate: number | null; // opened / sent
  submitRate: number | null; // submitted / totalRecipients
};

function rate(n: number, base: number): number | null {
  return base > 0 ? Math.round((n / base) * 100) : null;
}

export function computeMetrics(dto: SurveyResultsDto): SurveyMetrics {
  const { recipients, sends } = dto;
  const totalRecipients = recipients.length;
  const submitted = recipients.filter((r) => r.responseStatus === 'submitted').length;
  const started = recipients.filter((r) => r.totalPageViews > 0).length;
  const pageViews = recipients.reduce((sum, r) => sum + (r.totalPageViews || 0), 0);
  const pending = totalRecipients - submitted;

  const sent = sends.length;
  // Unique recipients with ≥1 email-invite open.
  const openedRecipients = new Set<string>();
  for (const s of sends) if (s.channel === 'email' && s.openCount > 0) openedRecipients.add(s.recipientId);
  const opened = openedRecipients.size;

  return {
    sent,
    opened,
    started,
    submitted,
    pending,
    pageViews,
    openRate: rate(opened, sent),
    submitRate: rate(submitted, totalRecipients),
  };
}

// ---------- per-channel breakdown (§6.3) ----------

export type ChannelStats = {
  sent: number; // provider-accepted (sent + delivered + bounced)
  delivered: number;
  opened: number | null; // email-only; null → render em-dash (SMS)
  clicked: number | null; // email-only
  bounced: number;
  failed: number;
  unsubscribed: number;
};

// Counter logic mirrors the broadcast channelCounters (visual + semantic parity).
function channelStats(sends: SurveyResultsDto['sends'], emailLike: boolean): ChannelStats {
  let sent = 0;
  let delivered = 0;
  let bounced = 0;
  let failed = 0;
  let opened = 0;
  let clicked = 0;
  let unsubscribed = 0;
  for (const s of sends) {
    if (s.status === 'sent' || s.status === 'delivered' || s.status === 'bounced') sent++;
    if (s.status === 'delivered') delivered++;
    if (s.status === 'bounced') bounced++;
    if (s.status === 'failed') failed++;
    if (s.openCount > 0) opened++;
    if (s.clickCount > 0) clicked++;
    if (s.unsubscribedAt) unsubscribed++;
  }
  return {
    sent,
    delivered,
    bounced,
    failed,
    unsubscribed,
    opened: emailLike ? opened : null,
    clicked: emailLike ? clicked : null,
  };
}

// Per-channel stats for the channels the survey actually used.
export function computeChannelStats(dto: SurveyResultsDto): { email?: ChannelStats; sms?: ChannelStats } {
  const out: { email?: ChannelStats; sms?: ChannelStats } = {};
  if (dto.survey.channels.includes('email')) {
    out.email = channelStats(dto.sends.filter((s) => s.channel === 'email'), true);
  }
  if (dto.survey.channels.includes('sms')) {
    out.sms = channelStats(dto.sends.filter((s) => s.channel === 'sms'), false);
  }
  return out;
}

// Recipients that haven't submitted — the "Pending" card's target set (Phase F
// resend). Excludes expired (the survey closed on them).
export function pendingRecipients(dto: SurveyResultsDto): SurveyResultRecipient[] {
  return dto.recipients.filter((r) => r.responseStatus !== 'submitted');
}

// ---------- audience-dimension breakdowns (§6.5) ----------

// Client-side re-slice of the submitted responses by AM / brand / location type
// (using the response snapshot fields — no account re-resolution, constraint #1).
export type AudienceFilter = {
  amUid: string | null;
  brandId: string | null;
  accountType: AccountType | null;
};

export const EMPTY_AUDIENCE_FILTER: AudienceFilter = { amUid: null, brandId: null, accountType: null };

export type AudienceFilterOptions = {
  ams: { uid: string; name: string }[];
  brands: { id: string; name: string }[];
  accountTypes: AccountType[];
};

// Distinct filter values present in the submitted responses (so options never
// vanish as the user narrows). Derived from snapshots.
export function deriveFilterOptions(responses: SurveyResponse[]): AudienceFilterOptions {
  const ams = new Map<string, string>();
  const brands = new Map<string, string>();
  const accountTypes = new Set<AccountType>();
  for (const r of responses) {
    if (r.accountManagerUidSnapshot) ams.set(r.accountManagerUidSnapshot, r.accountManagerNameSnapshot ?? r.accountManagerUidSnapshot);
    if (r.brandIdSnapshot) brands.set(r.brandIdSnapshot, r.brandNameSnapshot || r.brandIdSnapshot);
    accountTypes.add(r.accountTypeSnapshot);
  }
  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
  return {
    ams: [...ams].map(([uid, name]) => ({ uid, name })).sort(byName),
    brands: [...brands].map(([id, name]) => ({ id, name })).sort(byName),
    accountTypes: [...accountTypes].sort(),
  };
}

export function applyAudienceFilter(responses: SurveyResponse[], filter: AudienceFilter): SurveyResponse[] {
  return responses.filter(
    (r) =>
      (filter.amUid === null || r.accountManagerUidSnapshot === filter.amUid) &&
      (filter.brandId === null || r.brandIdSnapshot === filter.brandId) &&
      (filter.accountType === null || r.accountTypeSnapshot === filter.accountType),
  );
}

export function isFilterActive(filter: AudienceFilter): boolean {
  return filter.amUid !== null || filter.brandId !== null || filter.accountType !== null;
}

// ---------- per-question aggregation (§6.4) ----------

// Submitted responses are the aggregation set (in-progress are excluded).
export function submittedResponses(dto: SurveyResultsDto): SurveyResponse[] {
  return dto.responses.filter((r) => r.submittedAt !== null);
}

export function stepsOf(dto: SurveyResultsDto): SurveyStep[] {
  return (dto.survey.sentSnapshot?.steps ?? dto.survey.steps) as SurveyStep[];
}

export type RespondentContext = { accountName: string; amName: string | null };

// Per-type result bodies (the fields aggregateBody returns).
type QuestionResultBody =
  | { type: 'yes_no'; yes: number; no: number }
  | { type: 'multiple_choice_single' | 'multiple_choice_multi'; allowMultiple: boolean; options: { id: string; label: string; count: number }[]; otherTexts: string[] }
  | { type: 'short_text' | 'long_text' | 'name'; entries: { text: string; ctx: RespondentContext }[] }
  | { type: 'rating_scale'; min: number; max: number; distribution: number[]; average: number; comments: { rating: number; comment: string; ctx: RespondentContext }[] }
  | { type: 'date'; buckets: { date: string; count: number }[] }
  | { type: 'number'; values: number[]; min: number; max: number; avg: number; median: number };

type QuestionCommon = { kind: 'question'; stepId: string; prompt: string; respondedCount: number };

// FLAT discriminated union (each question body flattened with the common fields)
// so callers narrow cleanly on kind + type — Extract<…, { type }> and
// control-flow narrowing both work (an intersection-with-union does not).
export type QuestionResult =
  | { kind: 'content'; stepId: string; title: string; viewCount: number }
  | (QuestionCommon & Extract<QuestionResultBody, { type: 'yes_no' }>)
  | (QuestionCommon & Extract<QuestionResultBody, { type: 'multiple_choice_single' | 'multiple_choice_multi' }>)
  | (QuestionCommon & Extract<QuestionResultBody, { type: 'short_text' | 'long_text' | 'name' }>)
  | (QuestionCommon & Extract<QuestionResultBody, { type: 'rating_scale' }>)
  | (QuestionCommon & Extract<QuestionResultBody, { type: 'date' }>)
  | (QuestionCommon & Extract<QuestionResultBody, { type: 'number' }>);

function ctxOf(r: SurveyResponse): RespondentContext {
  return { accountName: r.accountNameSnapshot, amName: r.accountManagerNameSnapshot };
}

// Aggregate every step across the given submitted responses (the caller passes a
// filtered subset for audience breakdowns, §6.5). Content-page views are derived
// via realizePath (G8). Returns results in step order.
export function computeQuestionResults(
  dto: SurveyResultsDto,
  responses: SurveyResponse[] = submittedResponses(dto),
): QuestionResult[] {
  const steps = stepsOf(dto);
  // Per response: answers-by-step + realized visited set (for content views +
  // skip detection).
  const prepared = responses.map((r) => ({
    r,
    byStep: new Map(r.answers.map((a) => [a.stepId, a])),
    visited: new Set(realizePath(steps, r.answers).visitedStepIds),
  }));

  return steps.map((step): QuestionResult => {
    if (step.kind === 'content') {
      const viewCount = prepared.filter((p) => p.visited.has(step.id)).length;
      return { kind: 'content', stepId: step.id, title: step.title, viewCount };
    }
    const answered = prepared
      .map((p) => ({ a: p.byStep.get(step.id), ctx: ctxOf(p.r) }))
      .filter((x): x is { a: NonNullable<typeof x.a>; ctx: RespondentContext } => Boolean(x.a));
    const respondedCount = answered.length;
    const base = { kind: 'question' as const, stepId: step.id, prompt: step.prompt, respondedCount };
    return { ...base, ...aggregateBody(step, answered) };
  });
}

function aggregateBody(
  step: QuestionStep,
  answered: { a: SurveyResponse['answers'][number]; ctx: RespondentContext }[],
): QuestionResultBody {
  switch (step.type) {
    case 'yes_no': {
      let yes = 0;
      let no = 0;
      for (const { a } of answered) if (a.value.type === 'yes_no') (a.value.value ? yes++ : no++);
      return { type: 'yes_no', yes, no };
    }
    case 'multiple_choice_single':
    case 'multiple_choice_multi': {
      const cfg = step.config.type === step.type ? step.config : { options: [] };
      const counts = new Map<string, number>();
      const otherTexts: string[] = [];
      for (const { a } of answered) {
        const ids = a.value.type === 'multiple_choice_single' ? [a.value.optionId] : a.value.type === 'multiple_choice_multi' ? a.value.optionIds : [];
        const other = a.value.type === 'multiple_choice_single' || a.value.type === 'multiple_choice_multi' ? a.value.otherText : null;
        for (const id of ids) counts.set(id, (counts.get(id) ?? 0) + 1);
        if (ids.includes('__other__') && other && other.trim()) otherTexts.push(other.trim());
      }
      const options = cfg.options.map((o) => ({ id: o.id, label: o.label, count: counts.get(o.id) ?? 0 }));
      options.sort((x, y) => y.count - x.count);
      return { type: step.type, allowMultiple: step.type === 'multiple_choice_multi', options, otherTexts };
    }
    case 'short_text':
    case 'long_text':
    case 'name': {
      const entries = answered
        .map(({ a, ctx }) => ({ text: a.value.type === step.type ? answerText(a.value) : '', ctx }))
        .filter((e) => e.text.trim() !== '');
      return { type: step.type, entries };
    }
    case 'rating_scale': {
      const cfg = step.config.type === 'rating_scale' ? step.config : { min: 1, max: 10 };
      const distribution = new Array(cfg.max - cfg.min + 1).fill(0);
      const comments: { rating: number; comment: string; ctx: RespondentContext }[] = [];
      let sum = 0;
      let n = 0;
      for (const { a, ctx } of answered) {
        if (a.value.type !== 'rating_scale') continue;
        const idx = a.value.rating - cfg.min;
        if (idx >= 0 && idx < distribution.length) distribution[idx]++;
        sum += a.value.rating;
        n++;
        if (a.comment && a.comment.trim()) comments.push({ rating: a.value.rating, comment: a.comment.trim(), ctx });
      }
      return { type: 'rating_scale', min: cfg.min, max: cfg.max, distribution, average: n ? Math.round((sum / n) * 10) / 10 : 0, comments };
    }
    case 'date': {
      const counts = new Map<string, number>();
      for (const { a } of answered) if (a.value.type === 'date') counts.set(a.value.date, (counts.get(a.value.date) ?? 0) + 1);
      const buckets = [...counts.entries()].map(([date, count]) => ({ date, count })).sort((x, y) => x.date.localeCompare(y.date));
      return { type: 'date', buckets };
    }
    case 'number': {
      const values = answered.flatMap(({ a }) => (a.value.type === 'number' && !Number.isNaN(a.value.number) ? [a.value.number] : []));
      const sorted = [...values].sort((x, y) => x - y);
      const sum = values.reduce((s, v) => s + v, 0);
      const median = sorted.length ? sorted[Math.floor((sorted.length - 1) / 2)]! : 0;
      return {
        type: 'number',
        values: sorted,
        min: sorted[0] ?? 0,
        max: sorted[sorted.length - 1] ?? 0,
        avg: values.length ? Math.round((sum / values.length) * 10) / 10 : 0,
        median,
      };
    }
  }
}

function answerText(v: SurveyResponse['answers'][number]['value']): string {
  if (v.type === 'short_text' || v.type === 'long_text') return v.text;
  if (v.type === 'name') return v.name;
  return '';
}

// Whether the dashboard should poll (survey still accepting responses).
export function isLiveStatus(status: SurveyStatus): boolean {
  return status === 'sending' || status === 'open';
}

export const SURVEY_STATUS_LABEL: Record<SurveyStatus, string> = {
  draft: 'Draft',
  sending: 'Sending',
  open: 'Open',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

export const SURVEY_STATUS_TONE: Record<SurveyStatus, string> = {
  draft: 'bg-slate-100 text-slate-600',
  sending: 'bg-amber-100 text-amber-700',
  open: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-slate-200 text-slate-700',
  cancelled: 'bg-red-100 text-red-700',
};
